import React, { useEffect } from 'react';
import './About.css';
import { motion, useMotionValue, useSpring, useAnimationFrame, animate } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { LinkSquare01Icon, Mail01Icon, NewTwitterIcon, InstagramIcon, Linkedin01Icon } from '@hugeicons-pro/core-stroke-sharp';
import transition from '../transition';
import { toCdn } from '../utils/cdn';

function About() {

    // Preload About page images; finish transition only after they're ready (or timeout)
    useEffect(() => {
        const urls = [
            toCdn('/images/about/family.png'),
            toCdn('/images/about/golfing.png'),
            toCdn('/images/about/portland.png'),
            toCdn('/images/about/meta.png'),
            toCdn('/images/about/surfing.png')
        ];
        let cancelled = false;

        const loadImage = (src) => new Promise((resolve) => {
            try {
                const img = new Image();
                img.decoding = 'async';
                img.loading = 'eager';
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = src;
            } catch (_) {
                resolve(false);
            }
        });

        const onDone = () => {
            if (cancelled) return;
            try { window.dispatchEvent(new Event('pageEnterReady')); } catch(_) {}
        };

        const timeout = new Promise((res) => setTimeout(res, 2000));
        Promise.race([Promise.all(urls.map(loadImage)), timeout]).then(onDone);

        return () => { cancelled = true; };
    }, []);
	return (

		<section id="section-about">

			<div className="about-title">
				{(() => {
					const words = ['Todd', 'Hamilton'];
					const container = {
						hidden: {},
						show: { transition: { staggerChildren: 0.04, delayChildren: 0.1 } }
					};
					const child = {
						hidden: { y: 120 },
						show: { y: 0, transition: { duration: 0.64, ease: [0.43, 0.13, 0.23, 0.96] } }
					};
					return (
						<motion.h1 variants={container} initial="hidden" animate="show" style={{ display: 'inline-block' }}>
							{words.map((word, wi) => (
								<React.Fragment key={wi}>
									<motion.span className={`title-word word-${wi}`} style={{ display: 'inline-block' }}>
										{Array.from(word).map((ch, i) => (
											<motion.span key={i} variants={child} style={{ display: 'inline-block' }}>{ch}</motion.span>
										))}
									</motion.span>
                                    {wi === 0 ? <span className="title-space">{'\u00A0'}</span> : null}
								</React.Fragment>
							))}
						</motion.h1>
					);
				})()}
			</div>

			<motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.5, ease: 'easeOut' }}>
			<div className="about-row">
				<div className="about-col background">
					<p>I'm a product designer and developer with over 20 years of experience. I've designed products for billions of users at <span className="about-highlight" data-photo="meta">Facebook</span>, where I spent a decade of my career. I'm passionate about prototyping and teaching other designers.</p>

					<p>When I'm not in front of a screen, you'll find me <span className="about-highlight" data-photo="surfing">surfing</span>, <span className="about-highlight" data-photo="golfing">golfing</span>, or hanging out with <span className="about-highlight" data-photo="family">family</span> and friends. I live in Maine in a small town outside of <span className="about-highlight" data-photo="portland">Portland</span>.</p>

					<p>Today, I'm building <a href="https://www.aiproxy.com" target="_blank" rel="noopener noreferrer">AIProxy <HugeiconsIcon icon={LinkSquare01Icon} size={11} color="currentColor" /></a>, a backend security tool that helps app developers protect API keys. AlProxy safely powers millions of AI requests per month and is used by thousands of developers.</p>
				</div>
				<div className="about-col">
					<PhotoCarousel3D />
				</div>
			</div>

            {/* Job list under timeline */}
            <div className="experience-row">
                <JobList endYear={2025} />

				<div className="experience-timeline">
					<TimelineRamp startYear={2004} endYear={2025} />
				</div>
            </div>

            <div className="about-protoyping">
				<h2>“If a picture is worth 1000 words, a prototype is worth 1000 meetings.” - IDEO</h2>
				<p>I think about prototyping a lot and its a big part of my design process. I love this quote because I've always believed the best way to explore an idea is to prototype it. During my time at Facebook, I made hundreds of prototypes using various tools—for user research, demos, and presentations. They helped me communicate ideas more clearly, spark better conversations, and align people across the company in a way static designs never could.</p>

				<p>Prototyping also opens the door to happy accidents. You stumble on interactions, animations, or flows you might never have uncovered if you were just designing static screens. Building and experimenting in motion invites discovery&ndash;and it's often in those small moments of play that the best ideas emerge.</p>
            </div>

			<div className="about-contact">
				<div className="about-contact-content">
					<h3>Contact</h3>
					<p>I'm always looking for new opportunities to work on interesting projects. If you'd like to chat feel free to reach out.</p>
				</div>

				<div className="contact-buttons">
					<a href="https://twitter.com/toddham" title="Twitter" target="_blank" rel="noopener noreferrer">
						<HugeiconsIcon icon={NewTwitterIcon} size={20} />
					</a>
					<a href="https://instagram.com/toddham" title="Instagram" target="_blank" rel="noopener noreferrer">
						<HugeiconsIcon icon={InstagramIcon} size={20} />
					</a>
					<a href="https://linkedin.com/in/toddham" title="LinkedIn" target="_blank" rel="noopener noreferrer">
						<HugeiconsIcon icon={Linkedin01Icon} size={20} />
					</a>
					<a href="mailto:toddham@gmail.com" title="Email">
						<HugeiconsIcon icon={Mail01Icon} size={20} />
					</a>
				</div>
			</div>
			</motion.div>

		</section>
	)
}

function PhotoCarousel3D() {
	// Images from public/images/about/
    const images = React.useMemo(() => ([
        { id: 'family', src: toCdn('/images/about/family.png') },
        { id: 'golfing', src: toCdn('/images/about/golfing.png') },
        { id: 'portland', src: toCdn('/images/about/portland.png') },
        { id: 'meta', src: toCdn('/images/about/meta.png') },
        { id: 'surfing', src: toCdn('/images/about/surfing.png') },
    ]), []);

	// Motion values
	const rotateYTarget = useMotionValue(0);
	const rotateY = useSpring(rotateYTarget, { stiffness: 30, damping: 20, mass: 1 });
	const tiltXRaw = useMotionValue(0); // vertical tilt (up/down)
	const tiltZRaw = useMotionValue(0); // horizontal tilt (left/right)
	const tiltX = useSpring(tiltXRaw, { stiffness: 220, damping: 24, mass: 0.5 });
	const tiltZ = useSpring(tiltZRaw, { stiffness: 220, damping: 24, mass: 0.5 });

	// Auto-rotate ring slowly
	const pausedRef = React.useRef(false);
	useAnimationFrame((t, delta) => {
		// delta is ms
		const speedDegPerSec = 8; // slow rotation
		if (!pausedRef.current) {
			rotateYTarget.set(rotateYTarget.get() + (delta / 1000) * speedDegPerSec);
		}
	});

	// Global mouse tilt handler (whole window), with graceful leave/return
	useEffect(() => {
		// Disable mouse-driven tilt on mobile/touch devices and set a pleasant default tilt
		const isMobile = (typeof window !== 'undefined') && ((window.matchMedia && window.matchMedia('(max-width: 768px)').matches) || ('ontouchstart' in window));
		if (isMobile) {
			try {
				// gentle forward tilt with slight horizontal angle
				tiltXRaw.set(8);
				tiltZRaw.set(-6);
			} catch(_) {}
			return;
		}
		const maxTilt = 15; // degrees
		const onMove = (e) => {
			const cx = window.innerWidth * 0.5;
			const cy = window.innerHeight * 0.5;
			const dx = (e.clientX - cx) / Math.max(1, window.innerWidth * 0.5); // -1..1
			const dy = (cy - e.clientY) / Math.max(1, window.innerHeight * 0.5); // -1..1
			const clampedX = Math.max(-1, Math.min(1, dx));
			const clampedY = Math.max(-1, Math.min(1, dy));
			tiltZRaw.set(clampedX * maxTilt);
			tiltXRaw.set(clampedY * maxTilt);
		};
		const onOut = (e) => {
			// If leaving the window entirely, ease back to neutral
			if (!e.relatedTarget) {
				tiltXRaw.set(0);
				tiltZRaw.set(0);
			}
		};
		// Hover control for highlights -> rotate to matching photo id
        const onMouseOver = (e) => {
			const el = e.target.closest && e.target.closest('.about-highlight');
			if (!el) return;
			const id = el.getAttribute('data-photo');
			if (!id) return;
			const idx = images.findIndex((im) => im.id === id);
			if (idx < 0) return;
			pausedRef.current = true;
          const angle = (idx / images.length) * 360;
          // Rotate ring so target faces front (angle 0) using shortest path and faster spring
          const current = rotateYTarget.get();
          let target = -angle;
          // Normalize to nearest equivalent angle to minimize rotation distance
          while (target - current > 180) target -= 360;
          while (current - target > 180) target += 360;
          try {
            animate(rotateYTarget, target, { type: 'spring', stiffness: 260, damping: 26, mass: 0.6 });
          } catch(_) {
            rotateYTarget.set(target);
          }
		};
		const onMouseOutHL = (e) => {
			const el = e.target.closest && e.target.closest('.about-highlight');
			if (!el) return;
			pausedRef.current = false; // resume from current rotation
		};
		window.addEventListener('mousemove', onMove, { passive: true });
		window.addEventListener('mouseout', onOut, { passive: true });
		document.addEventListener('mouseover', onMouseOver, { passive: true });
		document.addEventListener('mouseout', onMouseOutHL, { passive: true });
		return () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseout', onOut);
			document.removeEventListener('mouseover', onMouseOver);
			document.removeEventListener('mouseout', onMouseOutHL);
		};
	}, [tiltXRaw, tiltZRaw, rotateYTarget, images]);

	// Layout
	const count = images.length;
	const radius = 180; // tighter ring to fit column and bring items closer
	const itemW = 150;
	const itemH = 200;

	return (
		<div className="photo-carousel-3d">
			{/* Centered stage that holds the ring */}
			<div className="photo-carousel-3d-stage" />
			<motion.div
				className="photo-carousel-3d-ring"
				style={{
					rotateX: tiltX,
					rotateY, // auto-rotation around ring axis
					rotateZ: tiltZ,
				}}
			>
				{images.map((im, i) => {
					const angle = (i / count) * 360;
					return (
						<div
							key={im.id}
							style={{
								position: 'absolute',
								left: -itemW / 2,
								top: -itemH / 2,
								width: itemW,
								height: itemH,
								transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
								transformStyle: 'preserve-3d',
								backfaceVisibility: 'visible',
								boxShadow: '0 4px 8px rgba(0,0,0,0.24)',
								borderRadius: 7,
								overflow: 'hidden',
								background: '#eaeaea'
							}}
						>
							<img src={im.src} alt="" width={itemW} height={itemH} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', backfaceVisibility: 'visible' }} />
						</div>
					);
				})}
			</motion.div>
		</div>
	);
}

export default transition(About);

// Shared jobs data builder
const getJobs = (endYear) => ([
    { name: 'AIProxy', role: 'Cofounder', start: { y: 2024, m: 0 }, end: { y: endYear, m: 11 } },
    { name: 'Facebook Design Studio', role: 'Design architect', start: { y: 2023, m: 0 }, end: { y: 2024, m: 0 } },
    { name: 'Facebook Marketplace', role: 'Design lead', start: { y: 2017, m: 0 }, end: { y: 2023, m: 0 } },
    { name: 'Facebook Platform Team', role: 'Design lead', start: { y: 2014, m: 0 }, end: { y: 2017, m: 0 } },
    { name: 'Joypad', role: 'Cofounder', start: { y: 2012, m: 0 }, end: { y: 2014, m: 0 } },
    { name: 'Bantam Live', role: 'Design engineer', start: { y: 2010, m: 0 }, end: { y: 2012, m: 0 } },
	{ name: 'Freelance', role: 'Web designer', start: { y: 2007, m: 0 }, end: { y: 2010, m: 0 } },
	{ name: 'MediaWhiz', role: 'Web designer', start: { y: 2006, m: 0 }, end: { y: 2007, m: 0 } },
	{ name: 'Freelance', role: 'Motion designer', start: { y: 2005, m: 0 }, end: { y: 2006, m: 0 } },
	{ name: 'Hatchling Studios', role: 'Motion designer', start: { y: 2004, m: 0 }, end: { y: 2005, m: 0 } }
]);

function TimelineRamp({ startYear = 2004, endYear = 2025 }) {
    const years = React.useMemo(() => {
        const a = [];
        for (let y = startYear; y <= endYear; y++) a.push(y);
        return a;
    }, [startYear, endYear]);
    const { ticks, yearStartIdx } = React.useMemo(() => {
        const t = [];
        const ysi = [];
        years.forEach((y) => {
            ysi.push(t.length);
            const monthsThisYear = 12;
            for (let m = 0; m < monthsThisYear; m++) {
                const isYear = m === 0;
                t.push({ year: y, month: m, isYear, idx: t.length });
            }
        });
        return { ticks: t, yearStartIdx: ysi };
    }, [years]);
	const numTicks = ticks.length;
    const TICK_W = 1; // must match CSS width
    const TICK_GAP = 12; // wider gap to stretch timeline significantly
	// Jobs to mark with dots (start/end at January of given year)
	const jobs = getJobs(endYear);
    const idxFor = React.useCallback((y, m) => {
        const yi = y - startYear;
        if (yi < 0) return 0;
        if (yi >= yearStartIdx.length) return numTicks - 1;
        const base = yearStartIdx[yi] || 0;
        const mm = Math.max(0, Math.min(m || 0, 11));
        return base + mm;
    }, [startYear, yearStartIdx, numTicks]);
    // Layout and animation constants
    const baseMonth = 36;
    const baseYear = 42;
    const rampRange = 10; // in ticks
    const strength = 0.75;

    // Derived sizes
    const contentWidthPx = (numTicks * (TICK_W + TICK_GAP));

    // Viewport + drag with momentum using Framer Motion
    const viewportRef = React.useRef(null);
    const [viewportW, setViewportW] = React.useState(0);
    useEffect(() => {
        const measure = () => {
            try { setViewportW((viewportRef.current?.getBoundingClientRect()?.width) || 0); } catch(_) {}
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);
    const x = useMotionValue(0);
    const half = (viewportW || 0) * 0.5;
    const constraints = {
        left: half - contentWidthPx,
        right: half
    };

    // Center-based ramp index (no mouse hover)
    const [centerIdx, setCenterIdx] = React.useState(0);
    useAnimationFrame(() => {
        const cx = -x.get() + (viewportW * 0.5);
        const idx = cx / (TICK_W + TICK_GAP);
        setCenterIdx(idx);
    });

    // Active tick/job driven by center line
    const clampIdx = (i) => Math.max(0, Math.min(numTicks - 1, Math.round(i)));
    const jobIndex = jobs.findIndex((jb) => {
        const s = idxFor(jb.start.y, jb.start.m);
        const e = idxFor(jb.end.y, jb.end.m);
        const h = clampIdx(centerIdx);
        return h >= s && h <= e;
    });
    const activeJob = jobIndex >= 0 ? jobs[jobIndex] : null;

    // Broadcast active job
    useEffect(() => {
        try {
            window.dispatchEvent(new CustomEvent('timelineActiveJob', {
                detail: { name: activeJob?.name || null, index: jobIndex }
            }));
        } catch(_){}
    }, [activeJob, jobIndex]);

    // Listen for requests to scroll to a specific job range
    useEffect(() => {
        const onScrollToJob = (e) => {
            const job = e?.detail?.job;
            if (!job) return;
            const sIdx = idxFor(job.start.y, job.start.m);
            const targetIdx = sIdx; // align centerline to job start
            const step = (TICK_W + TICK_GAP);
            const targetX = (viewportW * 0.5) - (targetIdx * step);
            const minX = constraints.left;
            const maxX = constraints.right;
            const clampedX = Math.max(minX, Math.min(maxX, targetX));
            try { animate(x, clampedX, { type: 'spring', stiffness: 160, damping: 24, mass: 0.8 }); } catch(_) {}
        };
        window.addEventListener('timelineScrollToJob', onScrollToJob);
        return () => window.removeEventListener('timelineScrollToJob', onScrollToJob);
    }, [viewportW, constraints.left, constraints.right, TICK_W, TICK_GAP, idxFor, x]);

    return (
        <div>
            {/* Scroll viewport with momentum drag */}
            <div
                ref={viewportRef}
                className="timeline-scroller"
                style={{ width: '100%', overflow: 'hidden', cursor: 'grab' }}
                onMouseEnter={() => { try { window.dispatchEvent(new CustomEvent('timelineHoverStart')); } catch(_) {} }}
                onMouseLeave={() => { try { window.dispatchEvent(new CustomEvent('timelineHoverEnd')); } catch(_) {} }}
            >
			<motion.div
                    style={{ x, width: contentWidthPx, willChange: 'transform' }}
                    drag="x"
                    dragConstraints={constraints}
                    dragElastic={0.1}
                    dragMomentum={true}
                    dragTransition={{ power: 0.2, timeConstant: 400 }}
                    onDragStart={() => { try { x.stop(); window.dispatchEvent(new CustomEvent('timelineDragStart')); } catch(_) {} }}
                    onDragEnd={() => { try { window.dispatchEvent(new CustomEvent('timelineDragEnd')); } catch(_) {} }}
                >

                    <div className="timeline-lines" style={{ position: 'relative', display: 'flex', alignItems: 'end', gap: TICK_GAP }}>
                        {ticks.map((t, i) => {
                            const base = t.isYear ? baseYear : baseMonth;
                            const d = Math.abs(i - centerIdx);
                            const falloff = Math.max(0, 1 - d / rampRange);
                            const scale = 1 + falloff * falloff * strength; // ramp centered in viewport
                            const baseColor = t.isYear ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.2)';
                            return (
                                <motion.div
                                    key={i}
                                    style={{ width: 1, height: base, background: baseColor, transformOrigin: 'bottom', borderRadius: 1, scaleY: scale }}
                                    transition={{ type: 'spring', stiffness: 120, damping: 10, mass: 0.6 }}
                                />
                            );
                        })}
                    </div>
                    {/* Year labels centered under the year ticks */}
                    <div className="timeline-dates" style={{ position: 'relative', height: 20 }}>
                        {years.map((y, yi) => {
                            const firstTickIndex = yearStartIdx[yi] || 0;
                            const leftPx = firstTickIndex * (TICK_W + TICK_GAP);
                            return (
                                <div key={y} style={{ position: 'absolute', left: leftPx, transform: 'translateX(-50%)', fontSize: 11, color: 'var(--color-tertiary)' }}>
                                    {y}
                                </div>
                            );
                        })}
                    </div>


                </motion.div>
                {/* Center guideline that stays fixed in the viewport */}
                <div className="timeline-centerline" />
            </div>
        </div>
    );
}

function JobList({ endYear = 2025 }) {
    const jobs = getJobs(endYear);
    const fmt = (y) => (y >= endYear ? 'Current' : y);
    const [isDragging, setIsDragging] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const [timelineHover, setTimelineHover] = React.useState(false);
    useEffect(() => {
        const onStart = () => setIsDragging(true);
        const onEnd = () => setIsDragging(false);
        const onActive = (e) => { try { setActiveIndex(typeof e.detail?.index === 'number' ? e.detail.index : -1); } catch(_) { setActiveIndex(-1); } };
        const onHoverStart = () => setTimelineHover(true);
        const onHoverEnd = () => setTimelineHover(false);
        window.addEventListener('timelineDragStart', onStart);
        window.addEventListener('timelineDragEnd', onEnd);
        window.addEventListener('timelineActiveJob', onActive);
        window.addEventListener('timelineHoverStart', onHoverStart);
        window.addEventListener('timelineHoverEnd', onHoverEnd);
        return () => {
            window.removeEventListener('timelineDragStart', onStart);
            window.removeEventListener('timelineDragEnd', onEnd);
            window.removeEventListener('timelineActiveJob', onActive);
            window.removeEventListener('timelineHoverStart', onHoverStart);
            window.removeEventListener('timelineHoverEnd', onHoverEnd);
        };
    }, []);
    return (
        <div className={`job-list${isDragging ? ' dragging' : ''}${timelineHover ? ' timeline-hover' : ''}`}>
            <div className="job-list-body">
                {jobs.map((j, i) => (
                    <div
                        className={`job-row${i === activeIndex ? ' active' : ''}`}
                        key={i}
                        onMouseEnter={() => { try { window.dispatchEvent(new CustomEvent('timelineScrollToJob', { detail: { job: j } })); } catch(_) {} }}
                    >
                        <div className="job-col job-dates">{j.start.y} – {fmt(j.end.y)}</div>
                        <div className="job-col job-name">{j.name}</div>
                        <div className="job-col job-role">{j.role}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
